import IVerifiedSender from "../interface/verified-sender";
import ISmsTemplate from "../interface/sms-template";
import IScheduler from "../interface/scheduler";
import IPublicTable from "../interface/public-table";
import IPublicForm from "../interface/public-form";
import IPlanning from "../interface/planning";
import IPDFTemplate from "../interface/pdf-template";
import IParamBased from "../interface/param-based";
import IModuleCard from "../interface/module-card";
import IModule from "../interface/module";
import IMigrationMapping from "../interface/migration-mapping";
import IListView from "../interface/list-view";
import IList from "../interface/list";
import IGdprPolicyItem from "../interface/gdpr-policy-item";
import IExcelExport from "../interface/excel-export";
import IEmailTemplate from "../interface/email-template";
import IEmailSignature from "../interface/email-signature";
import IEmailCustomTag from "../interface/email-custom-tag";
import IEmailButton from "../interface/email-button";
import IDataExportTemplate from "../interface/data-export-template";
import IDataConnection from "../interface/data-connection";
import IAppSetting from "../interface/app-setting";
import ParamStorage from "../enum/param-storage";
import ParamOption from "../enum/param-option";
import ErrorCode from "../enum/error-code";
import ParameterStorage from "../class/parameter-storage";

class ParameterInstance {

    private storages: ParameterStorage<IParamBased>[] = [];

    /* Public Methods */

    public async init(): Promise<void> {
        this.clear();
        await this.initStorage<IAppSetting<unknown>>(ParamStorage.AppSettings, ParamOption.Autoload);
        await this.initStorage<IEmailTemplate>(ParamStorage.EmailTpl, ParamOption.Autoload, ['body']);
        await this.initStorage<ISmsTemplate>(ParamStorage.SmsTpl, ParamOption.Autoload, ['body']);
        await this.initStorage<IEmailCustomTag>(ParamStorage.EmailCustomTags, ParamOption.Autoload, ['start', 'end']);
        await this.initStorage<IEmailButton>(ParamStorage.EmailButtons, ParamOption.Autoload);
        await this.initStorage<IEmailSignature>(ParamStorage.EmailSignatures, ParamOption.Autoload, ['html']);
        await this.initStorage<IGdprPolicyItem>(ParamStorage.EmailGdprPolicy, ParamOption.None);
        await this.initStorage<IModule>(ParamStorage.Modules, ParamOption.Autoload);
        await this.initStorage<IModuleCard>(ParamStorage.ModuleCards, ParamOption.Autoload);
        await this.initStorage<IPlanning>(ParamStorage.Plannings, ParamOption.Autoload);
        await this.initStorage<IList>(ParamStorage.Lists, ParamOption.Autoload);
        await this.initStorage<IListView>(ParamStorage.Views, ParamOption.Autoload);
        await this.initStorage<IExcelExport>(ParamStorage.ExcelExport, ParamOption.Autoload);
        await this.initStorage<IPublicTable>(ParamStorage.PublicTables, ParamOption.Autoload);
        await this.initStorage<IPublicForm>(ParamStorage.PublicForms, ParamOption.Autoload);
        await this.initStorage<IMigrationMapping>(ParamStorage.MigrationMappings, ParamOption.None);
        await this.initStorage<IVerifiedSender>(ParamStorage.VerifiedSenders, ParamOption.Autoload);
        await this.initStorage<IDataExportTemplate>(ParamStorage.DataExport, ParamOption.Autoload);
        await this.initStorage<IPDFTemplate>(ParamStorage.PdfTemplates, ParamOption.Autoload);
        await this.initStorage<IScheduler>(ParamStorage.Schedulers, ParamOption.Autoload);
        await this.initStorage<IDataConnection>(ParamStorage.DataConnections, ParamOption.Autoload);
        await this.initStorage<IDataConnection>(ParamStorage.SortOrders, ParamOption.Autoload);
    }

    public async clear(): Promise<void> {
        this.storages = [];
    }

    public get<T>(name: ParamStorage): ParameterStorage<T> {
        const storage: ParameterStorage<IParamBased> | null = this.storages.find(s => s.id === name) || null;
        if (storage) {
            return storage as ParameterStorage<T>;
        } else {
            throw ErrorCode.UnknownParamStorage;
        }
    }

    /* Private Methods */

    private async initStorage<T>(name: ParamStorage, option: ParamOption, ondemand: string[] = []): Promise<void> {
        const storage: ParameterStorage<T> = new ParameterStorage<T>(name, option, ondemand);
        await storage.init();
        this.storages.push(storage);
    }

}

const Parameter = new ParameterInstance();

export default Parameter;